/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { BREADCRUMB_JSONLD_ID, buildBreadcrumbJsonLd } from '../lib/breadcrumbSchema.js';
import { removeJsonLd, setJsonLd } from '../lib/jsonLdDom';
import { EXPLICIT_NOINDEX_ROBOTS, PAGE_CUSTOM_JSONLD_ID } from '../lib/pageSeoOverrides.js';
import { shareImagePathOf } from '../lib/shareImage.js';
import { brandTitle } from '../lib/templateVars.js';

const HOST = 'https://ere-vergelijk.nl';
/** What the SPA fallback says on every 404 (server/spaRenderer.ts). */
const NOT_FOUND_ROBOTS = 'noindex, nofollow';
/**
 * Marks a head tag as "this client-side code created it", so a later teardown
 * removes only its own work and never a tag that was baked into the HTML —
 * the environment-wide `noindex` on acceptance, or the per-route robots the
 * prerender writes. Exported because ArticlePage runs its own head effect
 * (it needs per-article schema this hook does not model) and must honour the
 * same rule; the two never run at once, and each cleans up the other's leftovers.
 */
export const SEO_MANAGED_ATTR = 'data-seo-managed';
const MANAGED_ATTR = SEO_MANAGED_ATTR;

export interface BreadcrumbStep {
  /** Localized breadcrumb label (e.g. "Kennisbank"). */
  label: string;
  /** Relative path on the site (e.g. "/nl/kennisbank"). */
  path: string;
}

export interface SEOOptions {
  /** Override the page key used to look up og_image / noindex / focus_keyword (defaults to titleKey without `_title`). */
  pageKey?: string;
  /** Breadcrumb trail ending at the current page (Home is prepended automatically). */
  breadcrumbs?: BreadcrumbStep[];
  /**
   * Literal, already-filled title — wins over the `seo.<titleKey>` lookup.
   * For pages whose title carries a proper noun (a province, an author) the
   * translated key is a template; the page fills it and passes the result.
   */
  titleText?: string;
  /** Same for the description. */
  descriptionText?: string;
  /**
   * Robots directive for a page the CMS has not switched off (e.g.
   * THIN_PAGE_ROBOTS from src/lib/indexPolicy.js). The CMS
   * `seo.<pageKey>_noindex` flag wins over it: the editor's explicit opt-out
   * also withholds link equity. The same order both server-side head builders
   * bake (src/lib/pageSeoOverrides.js), so hydration never rewrites the tag.
   */
  robots?: string;
  /**
   * The hreflang cluster of a page whose path differs per language — a term
   * page, whose English slug may be its own. Omitted: the current path under
   * both prefixes, as every static page has it. `null` for a language: that
   * URL is not a real page in that language (an untranslated begrip's /en/
   * serves the Dutch text), so it gets no alternate — and when it is the
   * language on screen, the page carries no cluster at all. The same rule
   * both head builders bake for such a stub page.
   */
  alternates?: { nl: string | null; en: string | null };
  /**
   * The language of the WORDS on the page when it differs from the URL's
   * (that same stub page): drives og:locale, as it does in the baked head.
   */
  contentLanguage?: 'nl' | 'en';
  /**
   * The page is the not-found page. Its head claims nothing about the URL —
   * no canonical, no hreflang cluster — and says `noindex, nofollow` (the CMS
   * `not_found_noindex` flag still wins with its own value). The same head the
   * SPA fallback serves a 404 with (server/seoMeta.ts), so a hard load and a
   * client-side move to a dead link end up alike.
   */
  notFound?: boolean;
  /**
   * The page's own share image (site-relative), used when the SEO tab names
   * none: a model page's picture (2026-10-21). Shared through its share crop
   * like an SEO-tab image — the chain the baked head follows
   * (src/lib/seoHead.js), so hydration keeps the baked og:image.
   */
  ogImage?: string | null;
}

function ensureEl<T extends HTMLElement>(selector: string, factory: () => T, managed: boolean): T {
  let el = document.querySelector(selector) as T | null;
  if (!el) {
    el = factory();
    if (managed) el.setAttribute(MANAGED_ATTR, '1');
    document.head.appendChild(el);
  }
  return el;
}

function setMetaContent(selector: string, factory: () => HTMLElement, value: string) {
  ensureEl(selector, factory, true).setAttribute('content', value);
}

function setLink(rel: string, attrs: Record<string, string>) {
  const extraSelector = Object.entries(attrs)
    .filter(([k]) => k !== 'href')
    .map(([k, v]) => `[${k}="${v}"]`)
    .join('');
  const el = ensureEl(
    `link[rel="${rel}"]${extraSelector}`,
    () => {
      const created = document.createElement('link');
      created.setAttribute('rel', rel);
      return created;
    },
    true
  );
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

/**
 * Build a BreadcrumbList JSON-LD payload from a list of steps. Always prepends
 * a "Home" entry that points at the language root.
 *
 * The implementation moved to src/lib/breadcrumbSchema.js (2026-09-01) so the
 * two head-injection mirrors can bake the identical node under the identical
 * id; this re-export keeps `import { buildBreadcrumbJsonLd } from
 * '../hooks/useSEO'` working for ArticlePage.
 */
export { buildBreadcrumbJsonLd };

/**
 * Dynamically updates page metadata for SEO.
 *
 * Reads per-page fields from the `seo` translation namespace when defined:
 * - `seo.${pageKey}_og_image`  — absolute URL or site-relative path for og:image/twitter:image
 * - `seo.${pageKey}_noindex`   — truthy ("true"/true) adds page-level noindex,nofollow
 *
 * `pageKey` defaults to `titleKey` with a trailing `_title` stripped.
 */
export function useSEO(titleKey: string, descriptionKey: string, options: SEOOptions = {}) {
  const { t, language } = useLanguage();
  const {
    pageKey: pageKeyOverride,
    breadcrumbs,
    titleText,
    descriptionText,
    robots,
    alternates,
    contentLanguage,
    notFound,
    ogImage: ownOgImage,
  } = options;
  const breadcrumbsDep = breadcrumbs ? JSON.stringify(breadcrumbs) : '';
  const alternatesDep = alternates ? JSON.stringify(alternates) : '';

  useEffect(() => {
    const pageKey = pageKeyOverride ?? titleKey.replace(/_title$/, '');
    const rawTitle = titleText ?? t(`seo.${titleKey}`);
    // i18n values may already include the brand suffix (e.g. "… | ERE-Vergelijk.nl"),
    // in which case appending again would double-brand the tab title — the
    // head's own rule (src/lib/templateVars.js).
    const title = brandTitle(rawTitle);
    const description = descriptionText ?? t(`seo.${descriptionKey}`);

    const ogImageKey = `seo.${pageKey}_og_image`;
    const ogImageRaw = t(ogImageKey);
    const ogImagePath =
      ogImageRaw && ogImageRaw !== ogImageKey && ogImageRaw !== ''
        ? ogImageRaw
        : ownOgImage || null;

    const noindexKey = `seo.${pageKey}_noindex`;
    const noindexRaw = t(noindexKey);
    const pageNoindex = noindexRaw === 'true';
    const robotsDirective = pageNoindex
      ? EXPLICIT_NOINDEX_ROBOTS
      : notFound
        ? NOT_FOUND_ROBOTS
        : (robots ?? null);

    document.title = title;
    document.documentElement.lang = language;

    setMetaContent(
      'meta[name="description"]',
      () => Object.assign(document.createElement('meta'), { name: 'description' }),
      description
    );

    // Always canonicalize to the apex host — prevents www/acc hostnames leaking
    // into canonical + hreflang tags when Google crawls a non-canonical mirror.
    const canonicalURL = HOST + window.location.pathname;
    if (notFound) {
      document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
    } else {
      setLink('canonical', { href: canonicalURL });
    }

    // Strip leading `/`, language segment, and any trailing slash so we can
    // rebuild URLs that match the server-side canonical (`/nl`, `/en`) instead
    // of leaking a homepage `/nl/` form into hreflang.
    const pathWithoutLang = window.location.pathname.replace(/^\/(nl|en)?\/?/, '');
    const alternatePaths = alternates ?? {
      nl: pathWithoutLang ? `/nl/${pathWithoutLang}` : '/nl',
      en: pathWithoutLang ? `/en/${pathWithoutLang}` : '/en',
    };
    // Removes baked and hook-made alternates alike: on a client-side move to
    // a stub page the previous page's cluster is still in <head>, and a stub
    // must not inherit a neighbour's translations.
    const removeAlternate = (hreflang: string) =>
      document
        .querySelectorAll(`link[rel="alternate"][hreflang="${hreflang}"]`)
        .forEach((el) => el.remove());
    if (notFound || alternatePaths[language] === null) {
      // Not a real page in this language: annotate nothing, in either direction.
      (['nl', 'en', 'x-default'] as const).forEach(removeAlternate);
    } else {
      (['nl', 'en'] as const).forEach((lang) => {
        const path = alternatePaths[lang];
        if (path) setLink('alternate', { hreflang: lang, href: `${HOST}${path}` });
        else removeAlternate(lang);
      });
      setLink('alternate', {
        hreflang: 'x-default',
        href: `${HOST}${alternatePaths.nl ?? window.location.pathname}`,
      });
    }

    const setOG = (property: string, value: string) =>
      setMetaContent(
        `meta[property="${property}"]`,
        () => {
          const el = document.createElement('meta');
          el.setAttribute('property', property);
          return el;
        },
        value
      );

    // An image of our own is shared as its share crop (src/lib/shareImage.js),
    // a 1200×630 JPEG like the default — the rule the baked head follows
    // (applyOgImageOverride in src/lib/pageSeoOverrides.js).
    const shareImagePath = ogImagePath ? shareImagePathOf(ogImagePath) : null;
    const ogImagePathResolved = shareImagePath ?? ogImagePath ?? '/og-image.jpg?v=10';
    const isKnownSizeOgImage = !ogImagePath || shareImagePath !== null;
    const ogImageUrl = ogImagePathResolved.startsWith('http')
      ? ogImagePathResolved
      : `${HOST}${ogImagePathResolved.startsWith('/') ? '' : '/'}${ogImagePathResolved}`;

    setOG('og:title', title);
    setOG('og:description', description);
    setOG('og:url', canonicalURL);
    // og:locale describes the copy, which on a stub page is the other language.
    const wordsLanguage = contentLanguage ?? language;
    setOG('og:locale', wordsLanguage === 'nl' ? 'nl_NL' : 'en_US');
    // A page in one language only (a stub, a 404) has no alternate locale.
    const oneLanguage = notFound || alternatePaths.nl === null || alternatePaths.en === null;
    if (oneLanguage) {
      document
        .querySelectorAll('meta[property="og:locale:alternate"]')
        .forEach((el) => el.remove());
    } else {
      setOG('og:locale:alternate', wordsLanguage === 'nl' ? 'en_US' : 'nl_NL');
    }
    setOG('og:image', ogImageUrl);
    setOG('og:image:alt', title);
    // The default og-image.jpg and every share crop are 1200×630 JPEGs
    // (Facebook/LinkedIn sweet spot). Emitting explicit dimensions lets
    // crawlers reserve layout before fetching the image, which improves
    // link-preview rendering. Any other image — another host, an SVG — may
    // have any size, and we don't track it.
    if (isKnownSizeOgImage) {
      setOG('og:image:width', '1200');
      setOG('og:image:height', '630');
      setOG('og:image:type', 'image/jpeg');
    } else {
      document
        .querySelectorAll(
          `meta[property="og:image:width"][${MANAGED_ATTR}="1"], meta[property="og:image:height"][${MANAGED_ATTR}="1"], meta[property="og:image:type"][${MANAGED_ATTR}="1"]`
        )
        .forEach((el) => el.remove());
    }

    const setTW = (name: string, value: string) =>
      setMetaContent(
        `meta[name="${name}"]`,
        () => Object.assign(document.createElement('meta'), { name }),
        value
      );
    setTW('twitter:card', 'summary_large_image');
    setTW('twitter:title', title);
    setTW('twitter:description', description);
    setTW('twitter:image', ogImageUrl);

    if (robotsDirective) {
      setMetaContent(
        'meta[name="robots"]',
        () => Object.assign(document.createElement('meta'), { name: 'robots' }),
        robotsDirective
      );
    } else {
      document.querySelector(`meta[name="robots"][${MANAGED_ATTR}="1"]`)?.remove();
    }

    // breadcrumbsDep (stringified breadcrumbs) only exists to feed the deps
    // array — use the live `breadcrumbs` reference for the actual value to
    // avoid a wasted JSON.parse/stringify round-trip.
    if (breadcrumbs && breadcrumbs.length > 0) {
      setJsonLd(BREADCRUMB_JSONLD_ID, buildBreadcrumbJsonLd(breadcrumbs, language));
    } else {
      removeJsonLd(BREADCRUMB_JSONLD_ID);
    }

    // Admin-authored extra JSON-LD for this page. Accepts a single object
    // or an array of objects; invalid JSON is silently ignored (the admin UI
    // surfaces parse errors inline, so we don't want to double-log here).
    // The bake carries the same node under the same id, so this replaces it
    // in place rather than adding a second one.
    const customRaw = t(`seo.${pageKey}_custom_schema`);
    const hasCustom =
      typeof customRaw === 'string' &&
      customRaw !== `seo.${pageKey}_custom_schema` &&
      customRaw.trim().length > 0;
    if (hasCustom) {
      try {
        const payload = JSON.parse(customRaw);
        setJsonLd(PAGE_CUSTOM_JSONLD_ID, payload);
      } catch {
        removeJsonLd(PAGE_CUSTOM_JSONLD_ID);
      }
    } else {
      removeJsonLd(PAGE_CUSTOM_JSONLD_ID);
    }

    return () => {
      removeJsonLd(PAGE_CUSTOM_JSONLD_ID);
    };
  }, [
    titleKey,
    descriptionKey,
    t,
    language,
    pageKeyOverride,
    breadcrumbsDep,
    titleText,
    descriptionText,
    robots,
    alternatesDep,
    contentLanguage,
    notFound,
    ownOgImage,
  ]);
}
